// =============================================================================
// pages/EmpanelledCAs.jsx — Admin: manage the empanelled CA directory
// =============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import '../styles/EmpanelledCAs.css';

function StatusPill({ role }) {
  if (['ca', 'admin'].includes(role)) return <span className="ec-pill ec-pill--active">CA · Ready</span>;
  return <span className="ec-pill ec-pill--warn">Needs CA Upgrade</span>;
}

// ── Search-and-add panel ──────────────────────────────────────────────────────
function AddCAPanel({ onAdded, onRemoved }) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting,  setActing]  = useState(null);
  const [error,   setError]   = useState('');
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    setLoading(true); setError('');
    try {
      const data = await api.searchEmpanelableCAs(q.trim());
      setResults(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { doSearch(''); }, [doSearch]);

  const handleChange = (e) => {
    setQuery(e.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(e.target.value), 300);
  };

  const handleAdd = async (user) => {
    setActing(user.id); setError('');
    try {
      const result = await api.addEmpanelledCA(user.id);
      onAdded(result);
      setResults(r => r.map(x => x.id === user.id
        ? { ...x, is_empanelled: true, empanelled_id: result.id }
        : x
      ));
    } catch (e) {
      setError(e.message);
    } finally {
      setActing(null);
    }
  };

  const handleRemove = async (user) => {
    setActing(user.id); setError('');
    try {
      await api.removeEmpanelledCA(user.empanelled_id);
      onRemoved(user.empanelled_id);
      setResults(r => r.map(x => x.id === user.id
        ? { ...x, is_empanelled: false, empanelled_id: null }
        : x
      ));
    } catch (e) {
      setError(e.message);
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="ec-add-panel">
      <div className="ec-add-label">Search portal CA users</div>
      <input
        className="ec-input"
        type="text"
        placeholder="Search by name, email, or ICAI number…"
        value={query}
        onChange={handleChange}
        autoFocus
      />
      {error && <div className="ec-error">{error}</div>}
      {loading && <div className="ec-add-hint">Loading…</div>}
      {!loading && results.length === 0 && (
        <div className="ec-add-hint">
          {query ? 'No CA users match your search.' : 'No CA users found.'}
        </div>
      )}
      {results.length > 0 && (
        <div className="ec-search-results">
          {results.map(u => (
            <div key={u.id} className="ec-search-row">
              <div className="ec-search-info">
                <div className="ec-search-name">
                  {u.full_name || u.email}
                  {u.is_empanelled && <span className="ec-empanelled-badge">Empanelled</span>}
                </div>
                <div className="ec-search-meta">
                  {u.email}
                  {u.icai_member_id && <> · ICAI <span className="ec-mono">{u.icai_member_id}</span></>}
                  {u.mobile && <> · {u.mobile}</>}
                </div>
              </div>
              {u.is_empanelled ? (
                <button
                  className="ec-btn ec-btn--remove ec-btn--sm"
                  disabled={acting === u.id}
                  onClick={() => handleRemove(u)}
                >
                  {acting === u.id ? '…' : 'Remove'}
                </button>
              ) : (
                <button
                  className="ec-btn ec-btn--primary ec-btn--sm"
                  disabled={acting === u.id}
                  onClick={() => handleAdd(u)}
                >
                  {acting === u.id ? 'Adding…' : 'Add to Panel'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function EmpanelledCAs({ userRole }) {
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [showAdd,     setShowAdd]     = useState(false);
  const [removing,    setRemoving]    = useState(null);
  const [error,       setError]       = useState('');
  const [pageSize,    setPageSize]    = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const debounceRef = useRef(null);

  const fetchRows = (q = '') => {
    setLoading(true); setError('');
    api.listEmpanelledCAs(q)
      .then(data => { setRows(Array.isArray(data) ? data : []); setCurrentPage(1); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  // Load on mount
  useEffect(() => { fetchRows(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchRows(q), 300);
  };

  const handleRemoveFromTable = async (id, name) => {
    if (!window.confirm(`Remove ${name || 'this CA'} from the empanelled panel?`)) return;
    setRemoving(id);
    try {
      await api.removeEmpanelledCA(id);
      setRows(r => r.filter(x => x.id !== id));
    } catch (e) {
      alert(e.message);
    } finally {
      setRemoving(null);
    }
  };

  if (!userRole?.isAdmin) {
    return <div className="ec-wrap"><p className="ec-error">Admin access required.</p></div>;
  }

  return (
    <div className="ec-wrap">
      <div className="ec-header">
        <div>
          <h2 className="ec-title">Empanelled CAs</h2>
          <p className="ec-subtitle">
            CAs on this panel can be assigned to customer engagement requests.
          </p>
        </div>
        <button
          className={`ec-btn ${showAdd ? 'ec-btn--secondary' : 'ec-btn--primary'}`}
          onClick={() => setShowAdd(v => !v)}
        >
          {showAdd ? 'Close' : '+ Add / Manage CAs'}
        </button>
      </div>

      {showAdd && (
        <AddCAPanel
          onAdded={(ca) => setRows(r => [ca, ...r])}
          onRemoved={(id) => setRows(r => r.filter(x => x.id !== id))}
        />
      )}

      <div className="ec-toolbar">
        <input
          className="ec-search"
          type="text"
          placeholder="Filter panel by name, email, ICAI…"
          value={search}
          onChange={handleSearch}
        />
        <div className="ec-toolbar-right">
          <div className="ec-page-size">
            <span>Show</span>
            <select
              className="ec-page-size-select"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>per page</span>
          </div>
          <span className="ec-count">
            {rows.length === 0 ? '0 CAs' : `${Math.min((currentPage - 1) * pageSize + 1, rows.length)}–${Math.min(currentPage * pageSize, rows.length)} of ${rows.length}`}
          </span>
        </div>
      </div>

      {error && <div className="ec-error ec-error--block">{error}</div>}

      {(() => {
        const totalPages  = Math.max(1, Math.ceil(rows.length / pageSize));
        const startIdx    = (currentPage - 1) * pageSize;
        const visibleRows = rows.slice(startIdx, startIdx + pageSize);
        return loading ? (
          <div className="ec-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="ec-empty">
            {search ? 'No CAs match your filter.' : 'No empanelled CAs yet. Click "+ Add / Manage CAs" to get started.'}
          </div>
        ) : (
          <>
            <div className="ec-table-wrap">
              <table className="ec-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>ICAI No.</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Added</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(r => (
                    <tr key={r.id} className="ec-row">
                      <td className="ec-name">{r.name || r.linked_user_name || '—'}</td>
                      <td className="ec-mono">{r.icai_number}</td>
                      <td>{r.email}</td>
                      <td>{r.phone || '—'}</td>
                      <td><StatusPill role={r.linked_user_role} /></td>
                      <td className="ec-date">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        }) : '—'}
                      </td>
                      <td>
                        <button
                          className="ec-btn ec-btn--remove"
                          disabled={removing === r.id}
                          onClick={() => handleRemoveFromTable(r.id, r.name || r.linked_user_name)}
                        >
                          {removing === r.id ? '…' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="ec-pagination">
                <button
                  className="ec-page-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ← Prev
                </button>
                <span className="ec-page-info">Page {currentPage} of {totalPages}</span>
                <button
                  className="ec-page-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}
